import { object, string } from 'yup';

import { formatAddress, formatForWire } from 'utilities/common';

import { MAX_LENGTH_ERROR_MESSAGE, NULLABLE_STRING } from 'modules/common/constants';

export default class Location {
  static schema = object().shape({
    formattedAddress: string(),
    city: NULLABLE_STRING.max(60, MAX_LENGTH_ERROR_MESSAGE),
    state: NULLABLE_STRING.max(60, MAX_LENGTH_ERROR_MESSAGE),
    country: NULLABLE_STRING.max(60, MAX_LENGTH_ERROR_MESSAGE),
  });

  constructor(wireData, countries = [], provinces = []) {
    const json = wireData || {};
    
    this.city = json.city || '';
    
    // V2 API uses provinceId and countryId (GUIDs)
    // Legacy used stateCode/countryCode (strings)
    if (json.provinceId) {
      // Try both lowercase 'id' and uppercase 'Id' for V2 API compatibility
      const province = provinces.find(p => p.id === json.provinceId || p.Id === json.provinceId);
      this.state = province ? (province.title || province.Title) : (json.state || '');
    } else {
      this.state = json.state || '';
    }

    if (json.countryId) {
      // Try both lowercase 'id' and uppercase 'Id' for V2 API compatibility
      const country = countries.find(c => c.id === json.countryId || c.Id === json.countryId);
      this.country = country ? (country.title || country.Title) : (json.country || '');
    } else {
      this.country = json.country || '';
    }
    
    // Use resolved state/country values for formatted address
    this.formattedAddress = formatAddress({ city: this.city, state: this.state, country: this.country });
  }

  get formattedCityState() {
    if (!this.state) {
      return this.city;
    }
    return `${this.city}, ${this.state}`;
  }

  static fromWireData(wireData, countries = [], provinces = []) {
    return new Location(wireData, countries, provinces);
  }

  static generateErrors(errors, prefix) {
    const locationErrors = [];
    const cityError = errors[`${prefix}City`];
    if (cityError) {
      locationErrors.push(cityError);
    }

    const stateError = errors[`${prefix}StateCode`];
    if (stateError) {
      locationErrors.push(stateError);
    }

    const countryError = errors[`${prefix}CountryCode`];
    if (countryError) {
      locationErrors.push(countryError);
    }

    return locationErrors.length > 0 ? locationErrors.join(', '): null;
  }

  toWireData() {
    return formatForWire({
      city: this.city,
      state: this.state,
      country: this.country,
    });
  }
}
